import React, { useEffect } from "react";
import { useRouter } from "next/navigation";
import { setFlowData } from "@/components/PageHome/redux/slice";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
  FlowProps,
  ICON_STYLE,
  MODAL_KEYS,
  MODAL_TYPES,
  renderModalContent,
} from "@/components/PageHome/components/OnboardingSection/Flows/helpers";
import flowSelector from "@/components/PageHome/redux/selectors/flowData";
import { Conceive as ConceiveAttr } from "@/components/PageHome/redux/types";
import { onBoardUser } from "@/components/PageHome/redux/actions";
import { dateToString } from "@/helpers";
import { MORE_LESS_CYCLES } from "@/components/PageHome/constants";
import { APPOINTMENT_URL, NON_ONBOARDED_STATUS, ONBOARDING_STATUS } from "@/helpers/constants";
import { buttonClickedEvent, dateAddedEvent, popupViewedEvent } from "@/helpers/TSDEvents";
import SegmentInfoSelector from "@/redux/slice/auth/selectors/userSegmentInfo";
import { Actions, ButtonClickedInterface } from "@/helpers/TSDEvents/interfaces";
import signedInStateSelector from "@/redux/slice/auth/selectors/signedInState";
import { SignedinState } from "@/redux/slice/auth/enums/SignedInState";
import refererSelector from "@/redux/slice/global/selectors/refererSelector";

const Preconception: React.FC<FlowProps> = ({ isModalOpen, setIsModalOpen, userAgent }) => {
  const dispatch = useAppDispatch();
  const flowData = useAppSelector(flowSelector) as ConceiveAttr;
  const signedInStatus = useAppSelector(signedInStateSelector);
  const SegmentInfoObject = useAppSelector(SegmentInfoSelector);
  const navSrcSelector = useAppSelector(refererSelector);

  const router = useRouter();

  const [selectedModel, setSelectedModel] = React.useState<number>(1);

  useEffect(() => {
    const closeFunction = FlowData[selectedModel][MODAL_KEYS.onClose];

    if (isModalOpen === false && closeFunction && typeof closeFunction === "function") {
      closeFunction();
    }
  }, [isModalOpen]);

  const preconceptionModals: Record<number, string> = {
    1: "Last Period Date Selection Popup - Preconception",
    2: "Average Cycle Selection Popup - Preconception",
    3: "Don't Know Last Period Date Popup - Preconception",
    4: "Closest Estimate Last Period Date Selection Popup - Preconception",
    5: "Don't Know Average Cycle Popup - Preconception",
    6: "Closest Estimate Average Cycle Selection Popup - Preconception",
    7: `Cycle Is ${flowData?.cycleLength === MORE_LESS_CYCLES[0] ? `Shorter` : `Longer`} Than Usual Popup - Preconception`,
    8: "Average Cycle Selection Popup - Preconception",
    9: "Don't Know Average Cycle Popup - Preconception",
  };

  useEffect(() => {
    if (isModalOpen) {
      // Hit popup viewed event
      popupViewedEvent({
        popup_name: preconceptionModals[selectedModel] || "",
        popup_position: userAgent?.isMobile ? "bottom" : "top",
        momverse_stage: SegmentInfoObject?.type ? ONBOARDING_STATUS[SegmentInfoObject?.type] : NON_ONBOARDED_STATUS,
        nav_src: navSrcSelector,
      });
    }
  }, [isModalOpen, selectedModel]);

  const dispatchOnBoarding = (): void => {
    dispatch(onBoardUser());

    if (setIsModalOpen) {
      setIsModalOpen(false);
    }
  };

  const handlePeriodDate = (date: Date): void => {
    if (date) {
      const dateString = dateToString(date);
      dispatch(setFlowData({ ...flowData, periodDate: dateString }));
    }
  };

  const handlePreconceptionButtonClickEvent = (props: { buttonName: string; position?: number }) => {
    const { buttonName, position } = props;
    let payload: ButtonClickedInterface = {
      button_name: buttonName,
      momverse_stage: SegmentInfoObject?.type ? ONBOARDING_STATUS[SegmentInfoObject?.type] : NON_ONBOARDED_STATUS,
      is_loggedin: signedInStatus === SignedinState.SIGNEDIN ? true : false,
    };

    if (position) {
      payload.button_position = position;
    }

    buttonClickedEvent(payload);
  };

  const FlowData = {
    1: {
      [MODAL_KEYS.type]: MODAL_TYPES.periodDate,
      [MODAL_KEYS.initialDate]: flowData?.periodDate,
      [MODAL_KEYS.btnClick]: (date: Date): void => {
        // Add Button Click and Date Added Event
        dateAddedEvent({
          action: flowData?.periodDate ? Actions.EDIT : Actions.ADD,
          momverse_stage: SegmentInfoObject?.type ? ONBOARDING_STATUS[SegmentInfoObject?.type] : NON_ONBOARDED_STATUS,
          is_loggedin: signedInStatus === SignedinState.SIGNEDIN ? true : false,
          date_type: "period date",
          date: date.getTime(),
        });
        handlePreconceptionButtonClickEvent({ buttonName: "Next", position: 1 });
        handlePeriodDate(date);
        setSelectedModel(2);
      },
      [MODAL_KEYS.ctaBelowText]: (
        <button
          onClick={(): void => {
            handlePreconceptionButtonClickEvent({
              buttonName: "I don't remember when I got my last period",
              position: 2,
            });
            setSelectedModel(3);
          }}
        >
          I don’t remember when I got my last period
        </button>
      ),
      [MODAL_KEYS.bgIcon]: { iconStyle: ICON_STYLE.style1, className: ["bgIconTopRight"] },
    },
    2: {
      [MODAL_KEYS.type]: MODAL_TYPES.periodCycle,
      [MODAL_KEYS.description]: "",
      [MODAL_KEYS.btnClick]: (cycleLength: number): void => {
        handlePreconceptionButtonClickEvent({ buttonName: "Submit", position: 1 });

        if (typeof cycleLength === "number") {
          dispatch(setFlowData({ ...flowData, cycleLength: cycleLength }));
        }

        if (cycleLength <= 20 || cycleLength >= 36) {
          setSelectedModel(7);
        } else {
          dispatchOnBoarding();
        }
      },
      [MODAL_KEYS.ctaBelowText]: (
        <button
          onClick={(): void => {
            handlePreconceptionButtonClickEvent({ buttonName: "I don't remember my average cycle", position: 2 });
            setSelectedModel(5);
          }}
        >
          I don’t remember my average cycle
        </button>
      ),
      [MODAL_KEYS.bgIcon]: { iconStyle: ICON_STYLE.style1, className: ["bgIconTopRight"] },
      [MODAL_KEYS.onClose]: dispatchOnBoarding,
    },
    3: {
      [MODAL_KEYS.heading]: {
        prefixText: (
          <span>
            Don’t know your <strong>last period dates?</strong>
          </span>
        ),
      },
      [MODAL_KEYS.description]: {
        prefixText: (
          <span>
            Share your <strong>closest estimate</strong> for now and make edits to it later
          </span>
        ),
      },
      [MODAL_KEYS.ctaText]: "Share estimated period date",
      [MODAL_KEYS.btnClick]: (): void => {
        handlePreconceptionButtonClickEvent({ buttonName: "Share estimated period date", position: 1 });
        setSelectedModel(4);
      },
      [MODAL_KEYS.ctaBelowText]: (
        <button
          onClick={() => {
            handlePreconceptionButtonClickEvent({ buttonName: "I don't know the closest estimate", position: 2 });
            dispatchOnBoarding();
          }}
        >
          I don’t know the closest estimate
        </button>
      ),
      [MODAL_KEYS.bgIcon]: { iconStyle: ICON_STYLE.style1, className: ["bgIconTopRight"] },
      [MODAL_KEYS.onClose]: dispatchOnBoarding,
    },
    5: {
      [MODAL_KEYS.heading]: {
        prefixText: (
          <span>
            Don’t know how long your <strong>average cycle is?</strong>
          </span>
        ),
      },
      [MODAL_KEYS.description]: {
        prefixText: (
          <span>
            Share your <strong>closest estimate</strong> for now and make edits to it later
          </span>
        ),
      },
      [MODAL_KEYS.ctaText]: "Next",
      [MODAL_KEYS.btnClick]: (): void => {
        handlePreconceptionButtonClickEvent({ buttonName: "Next", position: 1 });
        setSelectedModel(6);
      },
      [MODAL_KEYS.ctaBelowText]: (
        <button
          onClick={() => {
            handlePreconceptionButtonClickEvent({ buttonName: "I don't remember my last period", position: 2 });
            dispatchOnBoarding();
          }}
        >
          I don’t remember my last period
        </button>
      ),
      [MODAL_KEYS.bgIcon]: { iconStyle: ICON_STYLE.style1, className: ["bgIconTopRight"] },
      [MODAL_KEYS.onClose]: dispatchOnBoarding,
    },
    7: {
      [MODAL_KEYS.heading]: {
        prefixText: (
          <span>
            <strong>Cycle {`${flowData?.cycleLength === MORE_LESS_CYCLES[0] ? "shorter" : "longer"}`} </strong> than
            usual?
          </span>
        ),
      },
      [MODAL_KEYS.description]: {
        prefixText: (
          <span>
            This happens for various reasons. It might be a good idea to <strong>check in with your doctor</strong>,
            just to make sure everything is fine
          </span>
        ),
      },
      [MODAL_KEYS.ctaText]: "Book an appointment",
      [MODAL_KEYS.btnClick]: (): void => {
        handlePreconceptionButtonClickEvent({ buttonName: "Book an appointment", position: 1 });
        router.push(APPOINTMENT_URL);
      },
      [MODAL_KEYS.ctaBelowText]: (
        <button
          onClick={() => {
            handlePreconceptionButtonClickEvent({ buttonName: "I don't want to book an appointment", position: 2 });
            dispatchOnBoarding();
          }}
        >
          I don’t want to book an appointment
        </button>
      ),
      [MODAL_KEYS.bgIcon]: { iconStyle: ICON_STYLE.style1, className: ["bgIconTopRight"] },
      [MODAL_KEYS.onClose]: dispatchOnBoarding,
    },
  };
  FlowData[4] = {
    ...FlowData["1"],
    btnClick: (date: Date): void => {
      handlePreconceptionButtonClickEvent({ buttonName: "Next", position: 1 });
      dateAddedEvent({
        action: Actions.ADD,
        momverse_stage: SegmentInfoObject?.type ? ONBOARDING_STATUS[SegmentInfoObject?.type] : NON_ONBOARDED_STATUS,
        is_loggedin: signedInStatus === SignedinState.SIGNEDIN ? true : false,
        date_type: "period date",
        date: date.getTime(),
      });
      handlePeriodDate(date);
      setSelectedModel(8);
    },
    description: {
      prefixText: (
        <span>
          Tell us an estimate of <strong>when your last period started</strong> and know when you’re most fertile
        </span>
      ),
    },
    ctaBelowText: "",
  };
  FlowData[6] = { ...FlowData["2"], ctaBelowText: "" };
  FlowData[8] = {
    ...FlowData["2"],
    ctaBelowText: (
      <button
        onClick={(): void => {
          setSelectedModel(9);
        }}
      >
        I don’t remember my average cycle
      </button>
    ),
  };
  FlowData[9] = {
    ...FlowData["5"],
    ctaText: "Share estimated average cycle",
  };

  return renderModalContent(FlowData[selectedModel]);
};

export default Preconception;
